/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import { useRouter } from "next/router";
import React from "react";

import { StrapiTerminusArticle } from "@/types/strapi";

import { BorderRadiuses } from "@/tokens/border";
import { Colors } from "@/tokens/color";
import { TransitionEasings, TransitionSpeeds } from "@/tokens/motion";
import { Spacing } from "@/tokens/spacing";

import { Flag } from "@/ui/atoms/flag";
import { Link } from "@/ui/atoms/link";
import { Picture } from "@/ui/atoms/picture";
import { Text } from "@/ui/atoms/text";

import { useTypedTheme } from "@/util/hooks/theme_hooks";
import { buildStylesByBreakpoint } from "@/util/style_util";

interface ArticleListItemProps {
    article: StrapiTerminusArticle;
    className?: SerializedStyles;
    index: number;
}

export const ArticleListItem = React.forwardRef<
    HTMLDivElement,
    ArticleListItemProps
>((props, ref) => {
    const { article, className, index } = props;
    const theme = useTypedTheme();
    const router = useRouter();

    const handleChipClick = (e: React.MouseEvent, topicSlug: string) => {
        e.preventDefault();
        e.stopPropagation();

        router.push(`/topic/${topicSlug}`);
    };

    const listWrapperStyles = css(
        {
            borderBottom: `1px solid ${Colors[theme.ui.uiBorder]}`,
            cursor: "pointer",
            display: "flex",
            flexFlow: "row wrap",
            paddingBottom: Spacing["spacing-4"],
            paddingTop: Spacing["spacing-4"],
            position: "relative",
            zIndex: 1,
        },
        {
            "&::before": {
                borderRadius: BorderRadiuses.borderMedium,
                content: "''",
                inset: `${Spacing["spacing-2"]} -${Spacing["spacing-3"]}`,
                pointerEvents: "none",
                position: "absolute",
                transition: `background ${TransitionSpeeds.fast}ms ${TransitionEasings.easeIn}`,
                zIndex: -1,
            },
            "&:hover::before": {
                backgroundColor: Colors[theme.backgrounds.backgroundSecondary],
            },
            "&:last-of-type": {
                borderBottom: "none",
            },
        },
        className,
    );

    const titleContainerStyles = css(
        {
            scrollMarginTop: Spacing["spacing-18"], // offset for when scrolling to article on mobile
        },
        buildStylesByBreakpoint("flex", {
            extraSmall: "0 0 100%",
            medium: "0 0 70%",
            large: "0 0 50%",
        }),
    );

    const metadataContainerStyles = css(
        {
            display: "flex",
            justifyContent: "space-between",
        },
        buildStylesByBreakpoint("flex", {
            extraSmall: "0 0 100%",
            medium: "1",
        }),
        buildStylesByBreakpoint("marginLeft", {
            extraSmall: "0",
            medium: Spacing["spacing-4"],
        }),
        buildStylesByBreakpoint("marginTop", {
            extraSmall: Spacing["spacing-6"],
            medium: 0,
        }),
    );

    const chipStyles = css({
        "&:hover": {
            backgroundColor: Colors["lighten-20"],
        },
        display: "inline-block",
        marginRight: Spacing["spacing-2"],
        position: "relative", // This is needed to ensure the hover state of the flag works
    });

    const authorAvatarStyles = css({
        borderRadius: BorderRadiuses.borderRound,
        width: Spacing["spacing-8"],
    });

    return (
        <Link
            className={listWrapperStyles}
            href={`/terminus/${article.Slug}`}
            interactionType="hover-color-change"
        >
            <div css={titleContainerStyles} ref={ref}>
                <Text
                    marginBottom="ArticleListItem"
                    tag="h4"
                    themeKey="headlinePrimary"
                >
                    {article.Title}
                </Text>

                {article.Description && (
                    <Text fontSize="TEXT_1" themeKey="textSecondary">
                        {article.Description}
                    </Text>
                )}
            </div>

            <div css={metadataContainerStyles}>
                <div>
                    {article.Topics?.map((topic) => (
                        <Flag
                            className={chipStyles}
                            fontFamily="mono"
                            fontSize="TerminusTopicChip"
                            isUppercase={true}
                            key={`sortable-article-list::topic::${index}-${topic.Slug}`}
                            tag="span"
                            onClick={(e) => handleChipClick(e, topic.Slug)}
                        >
                            {topic.Name}
                        </Flag>
                    ))}
                </div>

                <div>
                    {article.Authors?.map(
                        (author) =>
                            author.Avatar && (
                                <Picture
                                    {...author.Avatar}
                                    alternativeText={author.Name}
                                    className={authorAvatarStyles}
                                    key={`sortable-article-list::avatar::${index}`}
                                />
                            ),
                    )}
                </div>
            </div>
        </Link>
    );
});
ArticleListItem.displayName = "ArticleListItem";
